import { useState } from "react";
import { ChevronDown, Phone, User } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import AddressSearch from "./AddressSearch";

const FIELDS = ["name", "address", "postcode", "lat", "lng", "delivery_contact_name", "delivery_contact_phone"];

export default function RestaurantEditor({ restaurant, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map((k) => [k, restaurant[k] ?? (k === "lat" || k === "lng" ? null : "")])));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/restaurants/${restaurant.id}`, form);
      toast.success("Restaurant details saved");
      setOpen(false);
      onSaved?.();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  const summary = [restaurant.address, restaurant.delivery_contact_name && `Delivery: ${restaurant.delivery_contact_name}${restaurant.delivery_contact_phone ? ` · ${restaurant.delivery_contact_phone}` : ""}`].filter(Boolean).join(" — ");

  return (
    <li className="py-2" data-testid={`restaurant-item-${restaurant.id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-left" data-testid={`restaurant-toggle-${restaurant.id}`}>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-900">{restaurant.name}</span>
          <span className={`block text-xs truncate ${summary ? "text-slate-500" : "text-amber-700"}`} data-testid={`restaurant-summary-${restaurant.id}`}>{summary || "No address yet — tap to add address & delivery contact"}</span>
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <form onSubmit={save} className="mt-3 grid gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`restaurant-form-${restaurant.id}`}>
          <div><Label className="text-xs">Restaurant name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="h-11 mt-1" data-testid={`restaurant-name-${restaurant.id}`} /></div>
          <div>
            <Label className="text-xs">Delivery address</Label>
            <div className="mt-1">
              <AddressSearch value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v, lat: null, lng: null }))}
                onPick={(r) => setForm((f) => ({ ...f, address: r.address, postcode: r.postcode || f.postcode, lat: r.lat, lng: r.lng }))} testId={`restaurant-address-${restaurant.id}`} />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Pick a suggestion to fill the postcode automatically, or type the full address yourself.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Postcode</Label><Input value={form.postcode} onChange={(e) => set("postcode", e.target.value)} className="h-11 mt-1 font-mono uppercase" data-testid={`restaurant-postcode-${restaurant.id}`} /></div>
            <div><Label className="text-xs flex items-center gap-1"><User className="h-3 w-3" /> Delivery contact</Label><Input value={form.delivery_contact_name} onChange={(e) => set("delivery_contact_name", e.target.value)} placeholder="Who receives deliveries" className="h-11 mt-1" data-testid={`restaurant-contact-name-${restaurant.id}`} /></div>
            <div><Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Contact phone</Label><Input type="tel" value={form.delivery_contact_phone} onChange={(e) => set("delivery_contact_phone", e.target.value)} placeholder="07…" className="h-11 mt-1" data-testid={`restaurant-contact-phone-${restaurant.id}`} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid={`restaurant-cancel-${restaurant.id}`}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-slate-900" data-testid={`restaurant-save-${restaurant.id}`}>{saving ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      )}
    </li>
  );
}
